import { Link } from 'react-router-dom';
import { blogPosts } from '../../../mocks/blogs';

export default function CompactBlogTeaser() {
  const featuredBlogs = blogPosts.slice(0, 3);

  return (
    <section className="py-20 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-10 right-10 w-40 h-40 bg-amber-300/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 left-10 w-48 h-48 bg-orange-300/20 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg mb-4">
            <i className="ri-book-open-fill text-orange-500"></i>
            <span className="text-sm font-bold text-gray-700">Expert Cat Knowledge</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-4">
            <span className="bg-gradient-to-r from-orange-600 to-rose-600 bg-clip-text text-transparent">
              Learn
            </span>{' '}
            from Cat Experts
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
            Discover tips, guides, and insights from veterinarians and cat behaviorists
          </p>
        </div>

        {/* Featured Blogs Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {featuredBlogs.map((blog, index) => (
            <Link
              key={blog.id}
              to={`/blog/post?id=${blog.id}`}
              className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Featured Image */}
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Category Badge */}
                <div className="absolute top-4 left-4 px-3 py-1 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-sm font-bold rounded-full shadow-lg">
                  {blog.category}
                </div>

                {/* Read Time Badge */}
                <div className="absolute top-4 right-4 px-3 py-1 bg-white/90 backdrop-blur-sm text-gray-900 text-sm font-bold rounded-full shadow-lg flex items-center gap-1">
                  <i className="ri-time-fill text-orange-500"></i>
                  <span>{blog.readTime}</span>
                </div>
              </div>

              {/* Blog Content */}
              <div className="p-6">
                {/* Author & Date */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-rose-400 flex items-center justify-center shadow-lg">
                    <i className="ri-user-fill text-white"></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-900">{blog.author}</div>
                    <div className="text-xs text-gray-500">{blog.date}</div>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-black text-gray-900 mb-3 line-clamp-2 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-orange-600 group-hover:to-rose-600 transition-all duration-300">
                  {blog.title}
                </h3>

                {/* Excerpt */}
                <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                  {blog.excerpt}
                </p>

                {/* CTA */}
                <div className="flex items-center gap-2 text-orange-600 font-bold group-hover:gap-4 transition-all duration-300">
                  <span>Read Full Article</span>
                  <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Topics Grid */}
        <div className="bg-white rounded-3xl shadow-xl p-8 mb-12">
          <h3 className="text-2xl font-black text-gray-900 mb-6 text-center">Popular Topics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-heart-pulse-fill', label: 'Health & Wellness', color: 'from-rose-500 to-pink-500' },
              { icon: 'ri-restaurant-fill', label: 'Nutrition Tips', color: 'from-orange-500 to-amber-500' },
              { icon: 'ri-emotion-happy-fill', label: 'Behavior Guide', color: 'from-amber-500 to-orange-500' },
              { icon: 'ri-scissors-cut-fill', label: 'Grooming Care', color: 'from-pink-500 to-rose-500' }
            ].map((topic) => (
              <Link
                key={topic.label}
                to="/blog"
                className="group flex flex-col items-center gap-3 p-4 rounded-2xl hover:bg-gradient-to-br hover:from-orange-50 hover:to-rose-50 transition-all duration-300"
              >
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${topic.color} flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300`}>
                  <i className={`${topic.icon} text-2xl text-white`}></i>
                </div>
                <span className="text-sm font-bold text-gray-700 text-center group-hover:text-orange-600 transition-colors duration-300">
                  {topic.label}
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Main CTA */}
        <div className="text-center">
          <Link
            to="/blog"
            className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-orange-500 to-rose-500 text-white text-xl font-black rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 whitespace-nowrap group"
          >
            <i className="ri-book-open-fill text-2xl"></i>
            <span>Explore All Articles</span>
            <i className="ri-arrow-right-line text-2xl group-hover:translate-x-2 transition-transform duration-300"></i>
          </Link>
          <p className="text-gray-600 mt-6 text-lg">
            Join 500K+ readers learning about cat care every month 📚
          </p>
        </div>
      </div>
    </section>
  );
}
